import { roundPercentage } from '@/utils/number';
import { getStyleVariableValue } from '@/utilz';
import { Spin } from 'antd';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  type PieLabelRenderProps,
  type PieProps,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

type PieChartData = {
  value: number;
  label: string;
};

type PaletteType = 'bright' | 'darkViolet' | 'pastel';

const PALETTES: Record<PaletteType, string[]> = {
  darkViolet: ['#d2504b', '#9c63ff', '#64c064', '#eead51'],
  pastel: ['#bb7862', '#70b3a1', '#ab394b', '#057d85', '#bb626a', '#dac599'],
  bright: [
    '#f8ca00',
    '#9d419c',
    '#70c92f',
    '#e97f02',
    '#bd1550',
    '#7e4452',
    '#9ab57e',
    '#36a3a6',
    '#d34a29',
    '#db7028',
    '#c3604f',
  ],
};

const getColorForIndex = (colors: string[], index: number) => {
  return colors[index % colors.length];
};

const renderLegendText = (value: string) => {
  return (
    <span
      style={{
        paddingLeft: '3px',
        verticalAlign: 'middle',
        color: getStyleVariableValue('--disabled-text'),
        lineHeight: '12px',
      }}
    >
      {value}
    </span>
  );
};

const CustomizedLabel = ({
  cx = 0,
  cy = 0,
  midAngle,
  outerRadius = 0,
  index = 0,
  percent = 0,
  withLegend,
  chartData: data,
  colors,
  isValuePercent,
}: Omit<PieLabelRenderProps, 'outerRadius'> & {
  // TODO: to fix outerRadius type check for answer later: https://github.com/recharts/recharts/discussions/5223
  isValuePercent?: boolean;
  colors: string[];
  chartData: PieChartData[];
  withLegend?: boolean;
}) => {
  const { label, value = 0 } = data[index] || {};
  const formattedValue = Math.trunc(value);

  const total = data?.reduce((acc, curr) => acc + curr.value, 0);
  const previousItemPercentage = ((data?.at(index - 1)?.value || 0) / total) * 100;
  const nextItemPercentage = ((data?.at(index + 1)?.value || 0) / total) * 100;

  if (value === 0) {
    return null;
  }

  // Hide label if angle is too small (< 1%) and labels overlap each other
  if (percent * 100 < 1 && (previousItemPercentage < 1 || nextItemPercentage < 1)) {
    return null;
  }

  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const LABEL_OFFSET_PERCENTAGE = 0.25; // 25% of the outer radius
  const delta = Math.abs(outerRadius * LABEL_OFFSET_PERCENTAGE);

  // Coordinates for the starting point of the label's connecting line (edge of the pie)
  const sx = cx + outerRadius * cos;
  const sy = cy + outerRadius * sin;

  // Coordinates for the middle of the connecting line
  const mx = cx + (outerRadius + delta) * cos;
  const my = cy + (outerRadius + delta) * sin;

  // Endpoint of the line connecting the label to the pie
  const LABEL_OFFSET_MULTIPLIER = 20;
  const ex = mx + Number(cos.toFixed(1)) * LABEL_OFFSET_MULTIPLIER;
  const ey = my;

  const textAnchor = cos >= 0 ? 'start' : 'end';

  const labelPrefix = withLegend ? '' : `${label}: `;
  const valueText = isValuePercent ? `${formattedValue}%` : `${formattedValue}`;

  const labelText = `${labelPrefix}${valueText}`;
  const labelColor = getColorForIndex(colors, index);

  const LABEL_TEXT_HORIZONTAL_OFFSET = 5;
  const LABEL_TEXT_VERTICAL_OFFSET = 4;

  return (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={labelColor} fill="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * LABEL_TEXT_HORIZONTAL_OFFSET}
        y={ey + LABEL_TEXT_VERTICAL_OFFSET}
        textAnchor={textAnchor}
        style={{ fontSize: '12px', fontWeight: 600 }}
        fill={labelColor}
      >
        {labelText}
      </text>
    </g>
  );
};

interface MyPieChartProps extends Pick<PieProps, 'outerRadius' | 'innerRadius'> {
  data: PieChartData[];
  palette?: PaletteType;
  withLegend?: boolean;
  isValuePercent?: boolean;
}

const MyPieChart = ({
  data = [],
  palette = 'bright',
  withLegend,
  outerRadius = 55,
  innerRadius,
  isValuePercent,
}: MyPieChartProps) => {
  const colors = PALETTES[palette];

  const getTooltipFormatter = (value: number, name: string) => {
    const totalSum = data?.reduce((acc, curr) => acc + curr.value, 0);
    const valueText = isValuePercent ? `${roundPercentage(value)}%` : `${value}`;
    const percentage = roundPercentage((value / totalSum) * 100);
    const percentageText = isValuePercent ? '' : ` (${percentage}%)`;

    return [valueText + percentageText, name];
  };

  return (
    <>
      {data?.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              label={(props) => (
                <CustomizedLabel
                  withLegend={withLegend}
                  chartData={data}
                  colors={colors}
                  isValuePercent={isValuePercent}
                  {...props}
                />
              )}
              labelLine={false}
              isAnimationActive
              animationDuration={1500}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${entry.label}`} fill={getColorForIndex(colors, index)} />
              ))}
            </Pie>
            {withLegend && (
              <Legend
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{
                  fontSize: 12,
                }}
                formatter={renderLegendText}
                iconType="square"
                iconSize={12}
              />
            )}
            <Tooltip
              formatter={getTooltipFormatter}
              contentStyle={{
                backgroundColor: getStyleVariableValue('--dark'),
                border: 0,
              }}
              itemStyle={{
                color: 'white',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Spin spinning={!data} />
      )}
    </>
  );
};

export default MyPieChart;
